import { useEffect, useMemo, useState } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { Device } from "common/device";
import { getStoredServiceId } from "common/app";
import { userStore } from "store/userStore";
import { recoverDataAtom, collapseToInitialAndPushRecoverStepAtom } from "store/recoverStore";
import { IdentityOtpForm, AuthType, IdentityOtpResult } from "components/identity/IdentityOtpForm";
import { getKrGenderDigit } from "utils/krGenderDigit";
import { toDanalCtn } from "utils/phoneNumber";

/**
 * Step3: OTP 인증 + find-account 호출
 *   - KR  → 다날 (DANAL_SELF)
 *   - 해외 → SMS OTP
 *
 * 인증 성공 후:
 *   - 본인인증 결과(verifyId / identityVerificationId) 저장
 *   - find-account 호출 (가입 이력 + provider + recoveryToken)
 *   - 응답 시 결과 페이지(Step4)로 collapse 진입 (device back → 초기 입력 화면)
 */
export const RecoverStep3 = () => {
    const [data, setData] = useAtom(recoverDataAtom);
    const collapseToInitialAndPush = useSetAtom(collapseToInitialAndPushRecoverStepAtom);
    const isKorea = useMemo(() => Device.countryCd === "KR", []);
    const findAccountState = useAtomValue(userStore.findAccount.stateAtom);

    const [findAccountSent, setFindAccountSent] = useState(false);

    const authType: AuthType = isKorea ? "DANAL_SELF" : "OTP";

    const handleOtpSuccess = (result: IdentityOtpResult) => {
        // 이전 흐름에서 findAccount store 가 ready/error 로 남아 있으면, findAccountSent=true 로
        //   바뀌는 순간 OTP 응답 전에 stale 데이터로 collapse 됨 → 먼저 default 로 리셋.
        userStore.findAccount.onDefault();
        setFindAccountSent(true);
        if (result.authType === "OTP" && result.verifyId !== undefined) {
            setData(prev => ({ ...prev, verifyId: result.verifyId ?? null }));
            userStore.onFindAccount({
                serviceId: getStoredServiceId(),
                ctn: data.ctn,
                ctnCountryCd: data.ctnCountryCd,
                verifyId: result.verifyId,
            });
        } else if (result.identityVerificationId) {
            setData(prev => ({ ...prev, identityVerificationId: result.identityVerificationId || "" }));
            userStore.onFindAccount({
                serviceId: getStoredServiceId(),
                ctn: data.ctn,
                ctnCountryCd: data.ctnCountryCd,
                identityVerificationId: result.identityVerificationId,
            });
        }
    };

    // find-account 응답 처리 — 결과 페이지로 collapse 진입
    //   wiz-sso AuthController 는 ResponseEntity<FindAccountResponse> 래퍼 없이 직접 반환 → state.data 가 곧 response body.
    useEffect(() => {
        if (!findAccountSent) return;
        if (findAccountState.status === "ready") {
            const d = findAccountState.data;
            setData(prev => ({
                ...prev,
                // SNS 가입자는 providerEmail 이 없을 수 있어 email=null 인 경우도 있음 → provider 로 판정
                accountFound: !!d?.provider,
                findAccountFailed: false,
                foundEmail: d?.email || "",
                foundProvider: d?.provider || "",
                foundSignedUpDate: d?.signedUpDate || "",
                recoveryToken: d?.recoveryToken || "",
            }));
            userStore.findAccount.onDefault();
            setTimeout(() => collapseToInitialAndPush(4), 0);
        } else if (findAccountState.status === "error") {
            // API 오류(서버/네트워크/본인인증 미완료) — '가입 이력 없음' 이 아니라 '오류' 로 구분.
            //   가입 이력 없음은 백엔드가 200 OK + provider 빈값 으로 반환하므로 ready 분기에서 처리됨.
            setData(prev => ({
                ...prev,
                accountFound: false,
                findAccountFailed: true,
                foundEmail: "",
                foundProvider: "",
                foundSignedUpDate: "",
                recoveryToken: "",
            }));
            userStore.findAccount.onDefault();
            setTimeout(() => collapseToInitialAndPush(4), 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [findAccountState.status, findAccountSent]);

    return (
        <IdentityOtpForm
            serviceId={getStoredServiceId()}
            authType={authType}
            danal={isKorea ? {
                name: data.name,
                // 저장 형식에는 선행 0 이 없다 — 다날 요청에만 국내 표기로 변환해 넘긴다.
                phoneNumber: toDanalCtn(data.ctn),
                birth: data.birth.substring(2),  // YYYYMMDD → YYMMDD
                genderDigit: getKrGenderDigit(data.birth, data.gender, data.nationality === "FOREIGNER"),
                carrier: data.telecom,
                parentConsent: false,
            } : undefined}
            sms={!isKorea ? {
                phoneNumber: data.ctn,
                countryCode: data.ctnCountryCd || "+82",
                // 비밀번호 찾기 → 비번 재설정 흐름의 일부이므로 PASSWORD_RESET 재사용
                // (SMS 발송/검증 동작은 verifyPurpose 와 무관하게 동일)
                verifyPurpose: "PASSWORD_RESET",
            } : undefined}
            onSuccess={handleOtpSuccess}
        />
    );
};
